import React, { useState, useCallback, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Upload, Play, Save, Trash2, Plus, Music, Film, Image as ImageIcon, Type, Wand2, ArrowLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { ReelTemplateSelector, REEL_TEMPLATES, ReelTemplate } from './ReelTemplates';
import EnhancedManifestationPlayer from './EnhancedManifestationPlayer';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface MediaFile {
  file: File | null;
  url: string;
  type: 'image' | 'video';
  duration?: number;
  existingPath?: string;
}

interface ManifestationReel {
  id: string;
  title: string;
  affirmations: string[];
  subliminal_words?: string[];
  media_files: any;
  audio_file?: string;
  template_id?: string;
  created_at: string;
}

interface ManifestationTemplate {
  id: string;
  title: string;
  affirmations: string[];
  subliminalWords: string[];
  sampleImages: string[];
  suggestedTemplate: any;
}

interface EnhancedManifestationCreatorProps {
  initialTemplate?: ManifestationTemplate;
}

export default function EnhancedManifestationCreator({ initialTemplate }: EnhancedManifestationCreatorProps = {}) {
  const [title, setTitle] = useState(initialTemplate?.title || '');
  const [selectedTemplate, setSelectedTemplate] = useState<ReelTemplate | null>(
    initialTemplate?.suggestedTemplate || REEL_TEMPLATES[0]
  );
  const [affirmations, setAffirmations] = useState(
    initialTemplate?.affirmations || ['', '', '']
  );
  const [subliminalWords, setSubliminalWords] = useState<string[]>(
    initialTemplate?.subliminalWords || ['']
  );
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [audioUrl, setAudioUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [savedReels, setSavedReels] = useState<ManifestationReel[]>([]);
  const [showPlayer, setShowPlayer] = useState(false);
  const [currentReel, setCurrentReel] = useState<any>(null);
  const [totalDuration, setTotalDuration] = useState(0);
  const { toast } = useToast();

  // Load sample images from template if provided
  useEffect(() => {
    if (initialTemplate?.sampleImages && mediaFiles.length === 0) {
      const sampleMedia: MediaFile[] = initialTemplate.sampleImages.map(url => ({
        file: null,
        url,
        type: 'image' as const,
        duration: 5,
        existingPath: undefined
      }));
      setMediaFiles(sampleMedia);
    }
  }, [initialTemplate]);

  // Quick affirmation prompts
  const quickPrompts = [
    "I am worthy of my dreams",
    "Abundance flows to me easily",
    "I attract success and prosperity",
    "My future is bright and limitless",
    "I am becoming my highest self"
  ];

  // Calculate total duration
  useEffect(() => {
    const total = mediaFiles.reduce((sum, file) => sum + (file.duration || 3), 0);
    setTotalDuration(total);
  }, [mediaFiles]);

  useEffect(() => {
    loadSavedReels();
  }, []);

  const handleMediaUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (mediaFiles.length + files.length > 10) {
      toast({
        title: "Too many files",
        description: "Maximum 10 media files allowed.",
        variant: "destructive"
      });
      return;
    }

    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    const videoFiles = files.filter(f => f.type.startsWith('video/'));

    // Compress images
    if (imageFiles.length > 0) {
      toast({
        title: 'Optimising images...',
        description: 'This may take a moment',
      });

      try {
        const { compressMultipleImages } = await import('@/utils/imageCompression');
        const compressed = await compressMultipleImages(imageFiles, 20);

        compressed.forEach(({ file, error }) => {
          if (!error) {
            const url = URL.createObjectURL(file);
            setMediaFiles(prev => [...prev, { file, url, type: 'image', duration: 3 }]);
          }
        });
      } catch (error) {
        imageFiles.forEach(file => {
          const url = URL.createObjectURL(file);
          setMediaFiles(prev => [...prev, { file, url, type: 'image', duration: 3 }]);
        });
      }
    }

    // Handle videos
    videoFiles.forEach(file => {
      const url = URL.createObjectURL(file);
      const video = document.createElement('video');
      video.src = url;
      video.onloadedmetadata = () => {
        const newTotal = totalDuration + video.duration;
        if (newTotal > 60) {
          toast({
            title: "Duration exceeded",
            description: `Total duration would be ${Math.round(newTotal)}s. Keep under 60s.`,
            variant: "destructive"
          });
          URL.revokeObjectURL(url);
          return;
        }
        setMediaFiles(prev => [...prev, { file, url, type: 'video', duration: video.duration }]);
      };
    });
  }, [mediaFiles.length, totalDuration, toast]);

  const handleAudioUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && (file.type === 'audio/mpeg' || file.type === 'audio/mp4')) {
      setAudioFile(file);
      setAudioUrl(URL.createObjectURL(file));
    } else {
      toast({
        title: "Invalid audio format",
        description: "Please upload MP3 or M4A files only.",
        variant: "destructive"
      });
    }
  }, [toast]);

  const removeMediaFile = (index: number) => {
    setMediaFiles(prev => {
      const newFiles = [...prev];
      URL.revokeObjectURL(newFiles[index].url);
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
    if (dragIndex === dropIndex) return;
    
    setMediaFiles(prev => {
      const newFiles = [...prev];
      const [draggedItem] = newFiles.splice(dragIndex, 1);
      newFiles.splice(dropIndex, 0, draggedItem);
      return newFiles;
    });
  };

  const addQuickAffirmation = (prompt: string) => {
    const emptyIndex = affirmations.findIndex(a => !a.trim());
    if (emptyIndex !== -1) {
      setAffirmations(prev => {
        const newAffirmations = [...prev];
        newAffirmations[emptyIndex] = prompt;
        return newAffirmations;
      });
    }
  };

  const previewReel = () => {
    if (mediaFiles.length === 0) {
      toast({
        title: "No media files",
        description: "Add at least one image or video to preview.",
        variant: "destructive"
      });
      return;
    }

    const reel = {
      title: title || 'Untitled Reel',
      affirmations: affirmations.filter(a => a.trim()),
      subliminal_words: subliminalWords.filter(w => w.trim()),
      media_files: mediaFiles.map(mf => ({
        type: mf.type,
        url: mf.url,
        duration: mf.duration
      })),
      audio_file: audioUrl,
      template: selectedTemplate,
      id: 'preview'
    };

    setCurrentReel(reel);
    setShowPlayer(true);
  };

  const saveReel = async () => {
    if (!title.trim()) {
      toast({
        title: "Title required",
        description: "Please enter a title for your reel.",
        variant: "destructive"
      });
      return;
    }

    if (mediaFiles.length === 0) {
      toast({
        title: "No media files",
        description: "Add at least one image or video.",
        variant: "destructive"
      });
      return;
    }

    if (totalDuration > 60) {
      toast({
        title: "Duration too long",
        description: `Your reel is ${Math.round(totalDuration)}s. Keep it under 60s.`,
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Upload media files
      const uploadedMediaFiles = await Promise.all(
        mediaFiles.map(async (mediaFile) => {
          if (mediaFile.file) {
            const fileName = `${user.id}/${Date.now()}-${mediaFile.file.name}`;
            const { error } = await supabase.storage
              .from('manifestation-media')
              .upload(fileName, mediaFile.file);
            
            if (error) throw error;
            
            return {
              type: mediaFile.type,
              path: fileName,
              duration: mediaFile.duration
            };
          }
          throw new Error('Invalid media file');
        })
      );

      // Upload audio file if present
      let audioFilePath = null;
      if (audioFile instanceof File) {
        const audioFileName = `${user.id}/${Date.now()}-${audioFile.name}`;
        const { error } = await supabase.storage
          .from('manifestation-audio')
          .upload(audioFileName, audioFile);
        
        if (error) throw error;
        audioFilePath = audioFileName;
      }

      // Save to database
      const { error } = await supabase.from('manifestations').insert({
        user_id: user.id,
        title: title.trim(),
        affirmations: affirmations.filter(a => a.trim()),
        subliminal_words: subliminalWords.filter(w => w.trim()),
        media_files: uploadedMediaFiles,
        audio_file: audioFilePath
      });

      if (error) throw error;

      toast({
        title: "Reel saved!",
        description: "Your manifestation reel has been saved."
      });

      // Reset form
      setTitle('');
      setAffirmations(['', '', '']);
      setSubliminalWords(['']);
      setMediaFiles([]);
      setAudioFile(null);
      setAudioUrl('');

      loadSavedReels();
    } catch (error) {
      console.error('Error saving reel:', error);
      toast({
        title: "Error saving",
        description: "Failed to save your reel. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadSavedReels = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('manifestations')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSavedReels(data || []);
    } catch (error) {
      console.error('Error loading reels:', error);
    }
  };

  const playReel = async (reel: ManifestationReel) => {
    try {
      // Load media URLs
      const mediaUrls = await Promise.all(
        reel.media_files.map(async (mediaFile: any) => {
          const { data, error } = await supabase.storage
            .from('manifestation-media')
            .createSignedUrl(mediaFile.path, 3600);
          
          if (error) {
            console.error('Error loading media:', error);
            return null;
          }
          return { ...mediaFile, url: data?.signedUrl };
        })
      );

      const validMediaUrls = mediaUrls.filter(m => m !== null);
      
      if (validMediaUrls.length === 0) {
        throw new Error('No media files could be loaded');
      }

      // Load audio URL if exists
      let audioUrl = '';
      if (reel.audio_file) {
        const { data, error } = await supabase.storage
          .from('manifestation-audio')
          .createSignedUrl(reel.audio_file, 3600);
        
        if (!error && data?.signedUrl) {
          audioUrl = data.signedUrl;
        }
      }

      const template = selectedTemplate || REEL_TEMPLATES[0];

      setCurrentReel({
        ...reel,
        media_files: validMediaUrls,
        audio_file: audioUrl,
        template
      });
      setShowPlayer(true);
    } catch (error) {
      console.error('Error loading reel:', error);
      toast({
        title: "Error loading reel",
        description: "Failed to load your reel. Please try again.",
        variant: "destructive"
      });
    }
  };

  const deleteReel = async (reelId: string, title: string) => {
    if (window.confirm(`Delete "${title}"? This cannot be undone.`)) {
      try {
        const { error } = await supabase
          .from('manifestations')
          .delete()
          .eq('id', reelId);

        if (error) throw error;

        toast({
          title: "Reel deleted",
          description: "Your reel has been permanently deleted."
        });

        loadSavedReels();
      } catch (error) {
        console.error('Error deleting reel:', error);
        toast({
          title: "Error deleting",
          description: "Failed to delete your reel.",
          variant: "destructive"
        });
      }
    }
  };

  if (showPlayer && currentReel) {
    return (
      <EnhancedManifestationPlayer
        reel={currentReel}
        onClose={() => setShowPlayer(false)}
      />
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6">
          <Tabs defaultValue="create" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="create">
                <Wand2 className="w-4 h-4 mr-2" />
                Create Reel
              </TabsTrigger>
              <TabsTrigger value="library">
                <Film className="w-4 h-4 mr-2" />
                My Reels ({savedReels.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="create" className="space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium mb-2">Reel Title</label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="My Manifestation Reel"
                  maxLength={100}
                />
              </div>

              {/* Template Selector */}
              <ReelTemplateSelector
                selectedTemplate={selectedTemplate}
                onSelect={setSelectedTemplate}
              />

              {/* Affirmations */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  <Type className="w-4 h-4 inline mr-2" />
                  Affirmations (appear as text overlays)
                </label>
                
                {/* Quick prompts */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {quickPrompts.map((prompt, i) => (
                    <Badge
                      key={i}
                      variant="outline"
                      className="cursor-pointer hover:bg-primary/10"
                      onClick={() => addQuickAffirmation(prompt)}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      {prompt}
                    </Badge>
                  ))}
                </div>

                {affirmations.map((affirmation, index) => (
                  <Textarea
                    key={index}
                    value={affirmation}
                    onChange={(e) => {
                      const newAffirmations = [...affirmations];
                      newAffirmations[index] = e.target.value;
                      setAffirmations(newAffirmations);
                    }}
                    placeholder={`Affirmation ${index + 1}`}
                    className="mb-2"
                    rows={2}
                    maxLength={150}
                  />
                ))}
              </div>

              {/* Media Upload */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  <ImageIcon className="w-4 h-4 inline mr-2" />
                  Media Files (Images & Videos)
                </label>
                <div className="flex items-center gap-2 mb-3">
                  <Button variant="outline" asChild>
                    <label className="cursor-pointer">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Media
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*,video/*"
                        multiple
                        onChange={handleMediaUpload}
                      />
                    </label>
                  </Button>
                  <div className="text-sm text-muted-foreground">
                    {mediaFiles.length}/10 files • {Math.round(totalDuration)}/60s
                  </div>
                </div>

                {/* Media preview */}
                {mediaFiles.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {mediaFiles.map((file, index) => (
                      <div
                        key={index}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => handleDrop(e, index)}
                        className="relative aspect-square rounded-lg overflow-hidden border-2 border-border cursor-move hover:border-primary transition-colors"
                      >
                        {file.type === 'image' ? (
                          <img src={file.url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <video src={file.url} className="w-full h-full object-cover" />
                        )}
                        <div className="absolute top-1 right-1">
                          <Button
                            size="sm"
                            variant="destructive"
                            className="h-6 w-6 p-0"
                            onClick={() => removeMediaFile(index)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                        <div className="absolute bottom-1 left-1">
                          <Badge variant="secondary" className="text-xs">
                            {Math.round(file.duration || 3)}s
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Audio Upload */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  <Music className="w-4 h-4 inline mr-2" />
                  Background Music (Optional)
                </label>
                <Button variant="outline" asChild>
                  <label className="cursor-pointer">
                    <Music className="w-4 h-4 mr-2" />
                    {audioFile ? 'Change Music' : 'Upload Music'}
                    <input
                      type="file"
                      className="hidden"
                      accept="audio/mpeg,audio/mp4"
                      onChange={handleAudioUpload}
                    />
                  </label>
                </Button>
                {audioFile && (
                  <p className="text-sm text-muted-foreground mt-2">
                    {audioFile.name}
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={previewReel}
                  variant="outline"
                  disabled={mediaFiles.length === 0}
                >
                  <Play className="w-4 h-4 mr-2" />
                  Preview
                </Button>
                <Button
                  onClick={saveReel}
                  disabled={isLoading || mediaFiles.length === 0 || !title.trim()}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {isLoading ? 'Saving...' : 'Save Reel'}
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="library">
              {savedReels.length === 0 ? (
                <div className="text-center py-12">
                  <Film className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">No saved reels yet</p>
                  <p className="text-sm text-muted-foreground">Create your first reel to get started!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {savedReels.map((reel) => (
                    <Card key={reel.id} className="overflow-hidden">
                      <div className="p-4">
                        <h3 className="font-semibold mb-2">{reel.title}</h3>
                        <p className="text-sm text-muted-foreground mb-3">
                          {reel.affirmations.length} affirmations • {reel.media_files.length} media files
                        </p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => playReel(reel)}
                          >
                            <Play className="w-3 h-3 mr-1" />
                            Play
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => deleteReel(reel.id, reel.title)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
